/**
 * Boot-time progress hydration (TECH-SPEC.md:1295 — "server = truth for
 * COMPLETION; local = truth for POSITION").
 *
 * The app has always written completions to the Sheet and never read them back,
 * so a learner who finished a lesson on the shop-floor tablet and then opened
 * the sandbox on their own phone saw an empty dashboard. The Sheet was
 * write-only from the app's point of view, which defeats the point of storing
 * progress centrally. This module closes that loop: one `GET /api/progress` at
 * startup, merged into the local store.
 *
 * WHY THIS IS NOT A REACT EFFECT. It runs at module scope from `src/main.tsx`,
 * following the precedent `src/i18n.ts:9` already sets for a boot-time read
 * outside the tree. There is no provider in this app to hang it off
 * (`grep createContext` returns nothing repo-wide), and `StrictMode`
 * double-invokes effects in development — a `useEffect` here would fire two
 * requests on every dev boot and make the once-only guard below load-bearing
 * for correctness rather than just for tidiness.
 *
 * It never throws and never blocks first paint. When the read fails the learner
 * simply continues on localStorage-only progress, which is exactly what they
 * had before this existed.
 */

import { fetchProgress } from '@/api/progress-client'
import { useLessonProgress } from '@/player/lesson-progress'
import { getSessionIdentity } from '@/session/identity'

/** Shared prefix so the two failure paths below cannot drift apart. */
const DEGRADED =
  'session/hydrate: could not restore server progress; continuing on local progress only'

/**
 * One hydration per page load. Set before the first `await`, so two synchronous
 * callers cannot both get past it.
 */
let hydrationStarted = false

/**
 * Pull this learner's completions from the server and merge them into the local
 * store. Safe to call more than once — every call after the first is a no-op.
 *
 * Returns a promise purely so tests can await the whole chain; production calls
 * it fire-and-forget (`void hydrateSession()` in `src/main.tsx`).
 */
export async function hydrateSession(fetchImpl: typeof fetch = fetch): Promise<void> {
  if (hydrationStarted) return
  hydrationStarted = true

  // BOOT-03 / TECH-SPEC §2.1: an unidentified session must not READ a record
  // either — without an employee id there is nobody to read for, and the only
  // id available to guess with would be someone else's. No request is made at
  // all. `getSessionIdentity()` rather than `isIdentified()` because it also
  // narrows the type, which `noNonNullAssertion` leaves no other way to do.
  const identity = getSessionIdentity()
  if (identity === null) return

  try {
    // `fetchProgress` is itself total — it has no rejection path. The try is
    // here for the line after it: `hydrateStages` calls zustand's `set`,
    // and `persist` writes `localStorage` unguarded (the same hazard
    // `TourOverlay.finish` wraps `markDone` for). A full quota or a locked-down
    // webview must not take the boot sequence down with it.
    const result = await fetchProgress(identity, fetchImpl)

    if (result.status !== 'ok') {
      // One line, once. A failed read changes nothing: local progress is left
      // exactly as it was, and the learner keeps working.
      console.warn(`${DEGRADED} — ${result.detail}`)
      return
    }

    // Handed over whole: every stage record the server returned, in the order
    // it returned them. Nothing is filtered, deduped or collapsed here —
    // deciding what a lesson's stages add up to is the store's job, and it
    // needs `stage`/`requiresUnguided` on each row to do it.
    useLessonProgress.getState().hydrateStages(result.progress.completions)
  } catch (err) {
    console.warn(`${DEGRADED} —`, err)
  }
}

/**
 * TEST SEAM — not production API, and not to be called from `src/` outside a
 * `*.test.*` file.
 *
 * Clears the once-only guard so each test can exercise a fresh boot. Mirrors
 * `resetSessionIdentityForTests()` in `./identity.ts`; like that one, it must
 * run in a `beforeEach`/`afterEach` or the first test's guard leaks into every
 * later test in the file.
 */
export function resetSessionHydrationForTests(): void {
  hydrationStarted = false
}
